import { useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Box as DarkBox, DarkProvider } from '@hivewatch/dark'

import { useFacilityArchivedFloorsQuery } from '@/graphql/generated/hooks'

import { DarkFloorPlanSidePanel } from '../../dark/DarkFloorPlanSidePanel'
import { FloorI } from '../../types/types'
import {
  mapFloor,
  sortFloorsByDisplayOrderThenName,
} from '../../utils/mappedFacilityData'
import { DarkFloorList } from './DarkFloorList'
import { DarkFloorListToggle } from './DarkFloorListToggle'
import { darkFloorsPanelStyles } from './DarkFloorsPanel.styles'
import { DarkFloorsPanelHeader } from './DarkFloorsPanelHeader'

export interface DarkFloorsPanelIProps {
  floors: FloorI[]
  currentFloorId?: string
  facilityId?: string
  isExpanded: boolean
  onToggleExpansion: () => void
  onReorderFloors?: (floorIds: string[]) => void
}

export const DarkFloorsPanel = ({
  floors,
  currentFloorId,
  facilityId,
  isExpanded,
  onToggleExpansion,
  onReorderFloors,
}: DarkFloorsPanelIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableUpdateFloor } = usePermissions()
  // The raw lock toggle, not useIsEditModeSatisfied — the Floor Plan keeps
  // explicit edit mode even with removeEditMode on (ENG-33745).
  const isEditMode = useIsEditMode()
  const isArchiveFloorEnabled = useFeatureFlag(
    FeatureFlag.archiveFloorAndFacility
  )
  const [shouldFilterFloorsWithIncidents, setFilterFloorsWithIncidents] =
    useState(false)
  const [shouldShowArchivedOnly, setShowArchivedOnly] = useState(false)

  // The panel's regular floors query filters to archived: false, so the
  // toggle opts into archived floors with its own query (legacy parity).
  const { data: archivedFloorsData } = useFacilityArchivedFloorsQuery({
    skip: !isArchiveFloorEnabled || !shouldShowArchivedOnly || !facilityId,
    variables: { facilityId },
    fetchPolicy: 'network-only',
  })

  const archivedFloors = useMemo(
    () =>
      (archivedFloorsData?.facility?.floors?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(Boolean)
        .sort(sortFloorsByDisplayOrderThenName)
        .map((floor) =>
          mapFloor(floor, archivedFloorsData?.facility?.defaultFloorId)
        ),
    [archivedFloorsData]
  )

  const baseFloors = shouldShowArchivedOnly ? archivedFloors : floors
  const filteredFloors = shouldFilterFloorsWithIncidents
    ? baseFloors?.filter((i) => i.hasIncidents)
    : baseFloors

  const isEditEnabled = shouldEnableUpdateFloor && isEditMode
  const canReorderFloors =
    isEditEnabled && !shouldFilterFloorsWithIncidents && !shouldShowArchivedOnly

  const s = darkFloorsPanelStyles(colorScheme)

  return (
    <DarkProvider>
      <DarkFloorPlanSidePanel
        dataTestid='floorPlanPage_darkFloorsPanel'
        header={<DarkFloorsPanelHeader colorScheme={colorScheme} />}
        isExpanded={isExpanded}
        onToggleExpansion={onToggleExpansion}
      >
        <DarkBox {...s.filters}>
          <DarkFloorListToggle
            checked={shouldFilterFloorsWithIncidents}
            colorScheme={colorScheme}
            label={t('floors:floors_panel_show_open_incidents')}
            onToggle={() =>
              setFilterFloorsWithIncidents(!shouldFilterFloorsWithIncidents)
            }
          />
          {isArchiveFloorEnabled && (
            <DarkFloorListToggle
              checked={shouldShowArchivedOnly}
              colorScheme={colorScheme}
              dataTestId='floorPlanPage_floorsPanel_archivedToggle'
              label={t('floors:floors_panel_show_archived_toggle')}
              onToggle={() => setShowArchivedOnly(!shouldShowArchivedOnly)}
            />
          )}
        </DarkBox>
        <DarkFloorList
          canReorderFloors={canReorderFloors}
          colorScheme={colorScheme}
          currentFloorId={currentFloorId}
          floors={filteredFloors}
          isEditEnabled={isEditEnabled}
          onReorderFloors={onReorderFloors}
        />
      </DarkFloorPlanSidePanel>
    </DarkProvider>
  )
}
